/**
 * Today screen.
 *
 * Reads /api/tasks/today (already filtered server-side to today-relevant
 * statuses + dates per the schema's "Today" view) and buckets the rows
 * into Overdue / Due / Followups / Scheduled. The "Focus" section from
 * the prototype isn't wired yet — there's no `pinned` flag on tasks.
 *
 * KPIs are computed live from the same response — no extra request.
 *
 * Keeps date math in this file (not the primitives) so the TaskRow
 * component stays purely presentational.
 */

import { Icon, type IconName } from '@k-os/ui';
import { useTasksToday, type TaskDto } from '../api/tasks';
import { TaskSection } from '../components/TaskSection';
import { useSectionSorts } from '../lib/task-sort';
import { useToggleTask } from './_use-open-task';
import { useTaskQuickActions } from './_use-task-quick-actions';
import { useSession } from '../api/auth-hooks';
import styles from './Today.module.css';

export function TodayScreen() {
  const query = useTasksToday();
  // `complete` knows from `task.status` whether to mark this task done or
  // re-open it; the mutation patches the React Query cache optimistically
  // so the row moves between groups before the server responds — that's
  // what makes the auto-animate transition fire cleanly.
  const complete = useToggleTask();
  const quick = useTaskQuickActions();
  const session = useSession();
  const firstName = session.data?.user.displayName.split(' ')[0] ?? null;
  // Must be called before the early isLoading/isError returns below so React
  // sees a stable hook order on every render.
  const sorts = useSectionSorts({
    'today.overdue': 'priority',
    'today.due': 'priority',
    'today.followups': 'priority',
    'today.scheduled': 'priority',
    'today.closed': 'priority',
  });

  if (query.isLoading) {
    return <div className={styles.loading}>Loading…</div>;
  }
  if (query.isError) {
    return (
      <div className={styles.error}>
        Couldn’t load Today. {(query.error as Error)?.message ?? 'Unknown error.'}
      </div>
    );
  }

  const tasks = query.data?.tasks ?? [];
  const buckets = bucket(tasks);
  const dateline = formatDateline(new Date());
  const kpis = {
    due: buckets.due.length,
    overdue: buckets.overdue.length,
    followups: buckets.followups.length,
    critical: tasks.filter((t) => t.priority === 'critical').length,
  };

  return (
    <>
      <div className={styles.greeting}>
        <div>
          <div className={styles.dateline}>{dateline}</div>
          <h2 className={styles.heading}>{greetingFor(firstName)}</h2>
          <div className={styles.subline}>
            {kpis.due} due · {kpis.followups} follow-ups · {kpis.overdue} overdue
          </div>
        </div>
      </div>

      <div className={styles.kpiGrid}>
        <Kpi label="Due today" value={kpis.due} icon="today" tint="accent" meta={kpis.critical ? `${kpis.critical} critical` : null} />
        <Kpi
          label="Overdue"
          value={kpis.overdue}
          icon="flag"
          tint="neg"
          critical={kpis.overdue > 0}
          meta={kpis.overdue > 0 ? 'needs triage' : null}
          metaAlert={kpis.overdue > 0}
        />
        <Kpi label="Follow-ups due" value={kpis.followups} icon="clock" tint="warn" />
        <Kpi label="Scheduled" value={buckets.scheduled.length} icon="upcoming" tint="pos" />
      </div>

      <TaskSection
        title="Overdue"
        alert={buckets.overdue.length > 0 ? 'needs attention' : null}
        tasks={buckets.overdue}
        sort={sorts.sortFor('today.overdue')}
        onSortChange={(next) => sorts.setSortFor('today.overdue', next)}
        onComplete={complete}
        quick={quick}
      />
      <TaskSection
        title="Due today"
        tasks={buckets.due}
        sort={sorts.sortFor('today.due')}
        onSortChange={(next) => sorts.setSortFor('today.due', next)}
        onComplete={complete}
        quick={quick}
      />
      <TaskSection
        title="Follow-ups due today"
        tasks={buckets.followups}
        showStatus
        sort={sorts.sortFor('today.followups')}
        onSortChange={(next) => sorts.setSortFor('today.followups', next)}
        onComplete={complete}
        quick={quick}
      />
      <TaskSection
        title="Scheduled"
        tasks={buckets.scheduled}
        sort={sorts.sortFor('today.scheduled')}
        onSortChange={(next) => sorts.setSortFor('today.scheduled', next)}
        onComplete={complete}
        quick={quick}
      />
      <TaskSection
        title="Closed"
        tasks={buckets.closed}
        sort={sorts.sortFor('today.closed')}
        onSortChange={(next) => sorts.setSortFor('today.closed', next)}
        onComplete={complete}
        quick={quick}
      />
    </>
  );
}

interface KpiProps {
  label: string;
  value: number;
  icon: IconName;
  tint: 'accent' | 'neg' | 'warn' | 'pos';
  critical?: boolean;
  meta?: string | null;
  metaAlert?: boolean;
}

const KPI_ICON_TINT: Record<KpiProps['tint'], string> = {
  accent: styles.kpiIconAccent!,
  neg: styles.kpiIconNeg!,
  warn: styles.kpiIconWarn!,
  pos: styles.kpiIconPos!,
};

function Kpi({ label, value, icon, tint, critical = false, meta, metaAlert = false }: KpiProps) {
  return (
    <div className={styles.kpi}>
      <div className={styles.kpiTop}>
        <div className={styles.kpiLabel}>{label}</div>
        <span className={`${styles.kpiIcon} ${KPI_ICON_TINT[tint]}`} aria-hidden>
          <Icon name={icon} size={15} />
        </span>
      </div>
      <div className={`${styles.kpiValue} ${critical ? styles.kpiCritical : ''}`}>{value}</div>
      {meta && <div className={`${styles.kpiMeta} ${metaAlert ? styles.kpiAlert : ''}`}>{meta}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bucketing + formatting
// ---------------------------------------------------------------------------

interface Buckets {
  overdue: TaskDto[];
  due: TaskDto[];
  followups: TaskDto[];
  scheduled: TaskDto[];
  closed: TaskDto[];
}

function bucket(tasks: TaskDto[]): Buckets {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  const overdue: TaskDto[] = [];
  const due: TaskDto[] = [];
  const followups: TaskDto[] = [];
  const scheduled: TaskDto[] = [];
  const closed: TaskDto[] = [];

  for (const t of tasks) {
    // Done rows on the Today payload are already filtered server-side to
    // "completed today", so we can bucket them directly without re-checking
    // the timestamp here.
    if (t.status === 'done') {
      closed.push(t);
      continue;
    }
    if (t.status === 'waiting' || t.status === 'delegated') {
      followups.push(t);
      continue;
    }
    const dueAt = t.dueAt ? Date.parse(t.dueAt) : null;
    const schedAt = t.scheduledAt ? Date.parse(t.scheduledAt) : null;
    // Overdue / Due today are driven by the DUE date and are mutually
    // exclusive with each other.
    if (dueAt && dueAt < startOfToday.getTime()) {
      overdue.push(t);
    } else if (dueAt && dueAt <= endOfToday.getTime()) {
      due.push(t);
    }
    // Scheduled is comprehensive: EVERY not-closed task scheduled for today
    // (or earlier) appears here, whether or not it has a due date — so a task
    // with an urgent due date can also appear under Overdue/Due above. (To
    // make the sections mutually exclusive again, gate this on the task not
    // already having landed in overdue/due.)
    if (schedAt && schedAt <= endOfToday.getTime()) {
      scheduled.push(t);
    }
  }
  return { overdue, due, followups, scheduled, closed };
}

function formatDateline(d: Date): string {
  return d
    .toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    .replace(',', ' ·');
}

/** Time-of-day-aware greeting that matches the design's "Good morning, Joao." copy. */
function greetingFor(firstName: string | null): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  return firstName ? `Good ${part}, ${firstName}.` : `Good ${part}.`;
}
